import React, { useLayoutEffect, useRef, useState } from 'react';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useTheme } from '@styledComponents';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { Elastic, gsap, Linear } from 'gsap';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';

export const useQuestionBuilderSectionController = () => {
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const multiSectionsContentRef = useRef<HTMLDivElement>(null);
  const secondSectionTopTextBlockRef = useRef<HTMLDivElement>(null);
  const secondSectionBottomTextBlockRef = useRef<HTMLDivElement>(null);
  const secondSectionRef = useRef<HTMLDivElement>(null);
  const questionCardsContainerRef = useRef<HTMLDivElement>(null);
  const questionCardShadowRef = useRef<HTMLDivElement>(null);
  const questionLottieRef = useRef<HTMLDivElement>(null);
  const privateQuestionLabelRef = useRef<HTMLDivElement>(null);
  const privateQuestionAvatarsDivRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();
  const isMobile = useThemeMediaQuery('below_sm');
  const { animationRef, loadAnimation } = useLottieAnimation(
    {
      path: cloudAssetUrl('wedding-invitations/customizable-questions/bodymovin/data.json'), // Required
      renderer: 'svg',
      loop: true,
      autoplay: false,
      name: 'homeHeroMovin',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: questionLottieRef }
  );

  useLayoutEffect(() => {
    let firstScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let secondScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let thirdScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let firstSceneTimeline: ReturnType<typeof gsap.timeline>;
    let secondSceneTimeline: ReturnType<typeof gsap.timeline>;
    let thirdSceneTimeline: ReturnType<typeof gsap.timeline>;
    const secondSectionEl = secondSectionRef.current;
    const multiSectionsContentEl = multiSectionsContentRef.current;
    const secondSectionTopTextBlockEl = secondSectionTopTextBlockRef.current;
    const secondSectionBottomTextBlockEl = secondSectionBottomTextBlockRef.current;
    const questionCardsContainerEl = questionCardsContainerRef.current;
    const questionCardShadowEl = questionCardShadowRef.current;
    const privateQuestionLabelEl = privateQuestionLabelRef.current;
    const privateQuestionAvatarsDivEl = privateQuestionAvatarsDivRef.current;
    let timeout: ReturnType<typeof setTimeout>;
    const refreshTimeline = () => {
      if (scrollController && multiSectionsContentEl && questionCardsContainerEl) {
        clearTimeout(timeout);

        timeout = setTimeout(() => {
          const scrollPos = scrollController?.scrollPos() as number;
          firstSceneTimeline?.progress(0).kill();
          secondSceneTimeline?.progress(0).kill();
          thirdSceneTimeline?.progress(0).kill();
          //
          initTimeline();
          if (firstScene) {
            firstScene.removePin(true);
            firstScene.setPin(multiSectionsContentEl, { pushFollowers: false });
            firstScene.update();
          }
          if (secondScene) {
            secondScene.update();
          }
          if (thirdScene) {
            thirdScene.removePin(true);
            thirdScene.setPin(multiSectionsContentEl, { pushFollowers: false });
            thirdScene.update();
          }
          scrollController.scrollTo(scrollPos);
        }, 300);
      }
    };
    let firstTlDirection = 'FORWARD';
    const initTimeline = () => {
      if (
        scrollController &&
        isMounted &&
        secondSectionEl &&
        multiSectionsContentEl &&
        secondSectionTopTextBlockEl &&
        secondSectionBottomTextBlockEl &&
        questionCardsContainerEl &&
        privateQuestionAvatarsDivEl &&
        privateQuestionLabelEl
      ) {
        firstSceneTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        // 0.01 - relatively small value to make call right after timeline scroll start
        firstSceneTimeline.call(
          () => {
            setSelectedQuestionIndex(0);
            animationRef.current?.goToAndStop(0, true);
            if (firstTlDirection === 'FORWARD') {
              animationRef.current?.play();
            }
          },
          [],
          0.01
        );
        firstSceneTimeline.to(questionCardsContainerEl, { scale: isMobile ? 1.15 : 1.4 }, 0);
        firstSceneTimeline.to(secondSectionTopTextBlockEl, { autoAlpha: 1, duration: 0.1 }, 0.4);
        firstSceneTimeline.to(questionCardShadowEl, { autoAlpha: 1 }, 0);
        secondSceneTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        secondSceneTimeline.to(secondSectionEl, { backgroundColor: theme.colors.green2 }, 0);
        secondSceneTimeline.to(questionCardShadowEl, { boxShadow: '0px 32px 64px rgba(201, 250, 212, 0.5)' }, 0);
        secondSceneTimeline.to(secondSectionBottomTextBlockEl, { autoAlpha: 1, duration: 0.1 }, 0.4);
        thirdSceneTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        thirdSceneTimeline.to(multiSectionsContentEl, { y: -80, duration: 1.5 }, 0);
        thirdSceneTimeline.to(privateQuestionLabelEl, { y: 0, opacity: 1, duration: 0.4, ease: Elastic.easeOut.config(1, 0.5) }, 0);
        thirdSceneTimeline.to(
          privateQuestionAvatarsDivEl.querySelectorAll('.private-question-avatar'),
          { scale: 1, opacity: 1, stagger: 0.2, duration: 0.5, delay: 0.4, ease: Elastic.easeOut.config(1, 0.4) },
          0
        );
      }
    };

    const initScenes = () => {
      if (
        secondSectionEl &&
        multiSectionsContentEl &&
        secondSectionTopTextBlockEl &&
        secondSectionBottomTextBlockEl &&
        questionCardsContainerEl &&
        scrollController
      ) {
        firstScene = new ScrollMagic.Scene({
          triggerElement: multiSectionsContentEl,
          triggerHook: 0.5,
          duration: isMobile ? 900 : 1100,
        })
          .setPin(multiSectionsContentEl, { pushFollowers: false })
          // .addIndicators()
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            firstSceneTimeline?.progress(event.progress);
            firstTlDirection = event.scrollDirection;
          });
        secondScene = new ScrollMagic.Scene({
          triggerElement: multiSectionsContentEl,
          offset: isMobile ? 900 : 1100,
          triggerHook: 0.5,
          duration: isMobile ? 200 : 300,
        })
          // .addIndicators()
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            secondSceneTimeline?.progress(event.progress);
          });
        thirdScene = new ScrollMagic.Scene({
          triggerElement: multiSectionsContentEl,
          offset: isMobile ? 800 : 1200,
          triggerHook: 0,
          duration: isMobile ? 200 : 300,
        })
          .setPin(multiSectionsContentEl, { pushFollowers: false })
          // .addIndicators()
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            thirdSceneTimeline?.progress(event.progress);
          });
      }
    };
    initTimeline();
    initScenes();
    //
    window.addEventListener('resize', refreshTimeline);
    const clearScene = () => {
      firstSceneTimeline?.progress(0).kill();
      secondSceneTimeline?.progress(0).kill();
      thirdSceneTimeline?.progress(0).kill();
      firstScene?.destroy(true);
      secondScene?.destroy(true);
      thirdScene?.destroy(true);
    };
    return () => {
      clearScene();
      window.removeEventListener('resize', refreshTimeline);
    };
  }, [scrollController, isMounted, isMobile, theme, animationRef]);

  const initAssets = () => {
    loadAnimation();
  };
  return {
    secondSectionRef,
    multiSectionsContentRef,
    secondSectionTopTextBlockRef,
    secondSectionBottomTextBlockRef,
    questionCardsContainerRef,
    questionCardShadowRef,
    questionLottieRef,
    privateQuestionLabelRef,
    privateQuestionAvatarsDivRef,
    initAssets,
    selectedQuestionIndex,
    setSelectedQuestionIndex,
  };
};
